
import React from 'react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Button } from '@/components/ui/button';

interface PaymentProcessingProps {
  sessionId?: string;
  pollCount?: number;
  maxPolls?: number;
  webhookProcessed?: boolean;
  onRetry?: () => void;
}

export const PaymentProcessing: React.FC<PaymentProcessingProps> = ({ 
  sessionId = '', 
  pollCount = 0, 
  maxPolls = 120,
  webhookProcessed = false,
  onRetry
}) => {
  const progressPercentage = Math.min((pollCount / maxPolls) * 100, 90); // Cap at 90% until complete
  const estimatedTimeRemaining = Math.max(0, Math.ceil((maxPolls - pollCount) * 4 / 60)); // minutes

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <LoadingSpinner className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {webhookProcessed ? "Processing Complete" : "Processing Your Payment"}
          </h1>
          <p className="text-gray-600">
            {webhookProcessed 
              ? "Your payment has been processed. Please check your email for confirmation."
              : "Please wait while we confirm your payment and create your reservation."
            }
          </p>
        </div>

        {!webhookProcessed && (
          <>
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Verifying payment...</span>
                <span>{Math.round(progressPercentage)}%</span>
              </div>
            </div>

            {/* Status Updates */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-800">Payment verification</span>
                <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse" />
              </div>
              
              {pollCount > 5 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm text-yellow-800">Creating reservation</span>
                  <div className="w-4 h-4 bg-yellow-500 rounded-full animate-pulse" />
                </div>
              )}
              
              {pollCount > 10 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-green-800">Sending confirmation</span>
                  <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse" />
                </div>
              )}
            </div>

            {/* Time Estimate */}
            {estimatedTimeRemaining > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                Estimated time remaining: {estimatedTimeRemaining} minute{estimatedTimeRemaining !== 1 ? 's' : ''}
              </p>
            )}
          </>
        )}

        {/* Session Info */}
        {sessionId && (
          <div className="text-xs text-gray-400 bg-gray-50 p-3 rounded-lg mb-4">
            <p>Session ID: {sessionId.slice(0, 20)}...</p>
            <p className="mt-1">
              {webhookProcessed 
                ? "Processing completed successfully."
                : "This process typically takes 1-2 minutes. Please don't close this window."
              }
            </p>
          </div>
        )}

        {/* Retry button for webhook processed state */}
        {webhookProcessed && onRetry && (
          <Button onClick={onRetry} className="mt-4">
            Check Status Again
          </Button>
        )}

        {/* Warning for long processing */}
        {pollCount > 60 && !webhookProcessed && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              Processing is taking longer than usual. If this continues, please contact support.
            </p>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm" className="mt-2">
                Retry
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
